import type { ChestPose } from './chest-mesh';
import {
  CHARGE_OPEN_S,
  CHARGE_S,
  LAND_FALL_S,
  clamp01,
  easeInCubic,
  easeOutCubic,
  spring,
  type ChestTimeline,
} from './chest-timeline';

/**
 * The chest's pose as a PURE function of a clock: where it stands, how it
 * turns, squashes and glows. The reveal (`revealPose`) and the arrival
 * (render/arrival-timeline.ts) share the idle's turn and the layout, so the
 * one chest reads as one object from its fall through its last card, and a
 * hand-over between the two is continuous by construction (tests:
 * render/arrival-timeline.test.ts).
 */

export interface ChestLayout {
  width: number;
  height: number;
  /** The chest's visual centre and its width, px. */
  x: number;
  y: number;
  size: number;
}

/** Where the chest sits for a viewport: centred, a little above the middle (the cards go under it). */
export function chestLayoutFor(width: number, height: number): ChestLayout {
  const portrait = height > width;
  const size = portrait
    ? Math.min(width * 0.56, height * 0.29)
    : Math.min(height * 0.3, width * 0.28, 420);
  return { width, height, x: width / 2, y: portrait ? height * 0.42 : height * 0.48, size };
}

/** The resting three-quarter turn (radians) and the camera's look down. */
export const BASE_YAW = -0.34;
export const BASE_PITCH = 0.3;
/** Rim light at rest. */
export const BASE_RIM = 0.55;

/** The baked still's pose (ChestScene.bakeStill): the chest's width as a fraction of the square, the lid cracked on its light. */
export const STILL_FILL = 0.5;
export const STILL_LID = 0.07;
export const STILL_EMIT = 0.6;
export const STILL_RIM = BASE_RIM + 0.3;

/** The idle's slow turn: the same sway in the arrival and the reveal. */
export function idleYaw(t: number): number {
  return BASE_YAW + Math.sin(t * 1.18) * 0.07;
}

/** The reveal's resting float (chest units). */
export function revealLift(t: number): number {
  return Math.sin(t * 2.6) * 0.018;
}

/** A pose at rest at a place. */
export function restPose(x: number, y: number, size: number, t: number): ChestPose {
  return {
    x,
    y,
    size,
    yaw: idleYaw(t),
    pitch: BASE_PITCH,
    roll: 0,
    sx: 1,
    sy: 1,
    lift: revealLift(t),
    lid: 0,
    flash: 0,
    rim: BASE_RIM,
    emit: 0,
  };
}

/** Blend two poses (a → b by k). */
export function mixPose(a: ChestPose, b: ChestPose, k: number): ChestPose {
  const m = (x: number, y: number) => x + (y - x) * k;
  return {
    x: m(a.x, b.x),
    y: m(a.y, b.y),
    size: m(a.size, b.size),
    yaw: m(a.yaw, b.yaw),
    pitch: m(a.pitch, b.pitch),
    roll: m(a.roll, b.roll),
    sx: m(a.sx, b.sx),
    sy: m(a.sy, b.sy),
    lift: m(a.lift, b.lift),
    lid: m(a.lid, b.lid),
    flash: m(a.flash, b.flash),
    rim: m(a.rim, b.rim),
    emit: m(a.emit, b.emit),
  };
}

/** How long a hand-over blends the arrival's last pose into the reveal's (s). */
export const HANDOFF_BLEND_S = 0.34;

/** The arrival's pose at the moment it handed the chest over, and when. */
export interface Handoff {
  at: number;
  from: ChestPose;
}

/**
 * The reveal's chest at the timeline's time. After a hand-over the reveal's
 * own pose is reached from the arrival's last one (`handoff.from`) over
 * HANDOFF_BLEND_S: at the hand-over frame it IS that pose — no cut.
 */
export function revealPose(tl: ChestTimeline, L: ChestLayout, handoff?: Handoff | null): ChestPose {
  const t = tl.t;
  const local = tl.local;
  const pose = restPose(L.x, L.y, L.size, t);
  if (tl.phase === 'land') {
    if (local < LAND_FALL_S) {
      const u = local / LAND_FALL_S;
      pose.lift = (1 - easeInCubic(u)) * 1.7;
      pose.yaw = BASE_YAW - 0.5 * (1 - u);
      pose.roll = 0.12 * (1 - u);
    } else {
      const s = spring(local - LAND_FALL_S, 16, 7);
      pose.sy = 1 - 0.2 * s;
      pose.sx = 1 + 0.12 * s;
      pose.lift = 0;
    }
  }
  if (tl.phase === 'charge') {
    const dur = tl.opening ? CHARGE_OPEN_S : CHARGE_S;
    const u = clamp01(local / dur);
    const e = easeOutCubic(u);
    const depth = tl.opening ? 0.2 : 0.14;
    pose.sy = 1 - depth * e;
    pose.sx = 1 + depth * 0.6 * e;
    pose.roll = Math.sin(local * 58) * 0.045 * u;
    pose.flash = 0.55 * u * u;
    pose.rim = BASE_RIM + 0.9 * u;
    pose.lift = 0;
  }
  if (tl.phase === 'upgrade') {
    const s = spring(local, 13, 6);
    pose.sy = 1 + 0.2 * s;
    pose.sx = 1 - 0.1 * s;
    pose.lift = Math.max(0, Math.sin(Math.PI * clamp01(local / 0.42))) * 0.16;
    pose.flash = Math.max(0, 1 - local / 0.3) * 0.9;
    pose.rim = BASE_RIM + 0.8 * Math.exp(-local * 5);
    pose.yaw = idleYaw(t) + 0.35 * spring(local, 10, 5);
  }
  if (tl.openAt !== null) {
    const o = t - tl.openAt;
    pose.lid = 2.0 * (1 - spring(o, 12, 6.5));
    pose.emit = Math.min(2.2, o * 9);
    const s = spring(o, 14, 7);
    pose.sy = 1 + 0.12 * s;
    pose.sx = 1 - 0.06 * s;
    pose.flash = Math.max(0, 0.8 - o * 3.2);
    pose.rim = 0.9;
    pose.yaw = -0.22 + Math.sin(t * 1.18) * 0.04;
    pose.lift = Math.sin(t * 2.6) * 0.012;
  }
  if (tl.phase === 'collect' && tl.collectAt !== null) {
    pose.emit = Math.max(0.6, 2.2 - (t - tl.collectAt) * 3);
  }
  if (handoff) {
    const k = clamp01((t - handoff.at) / HANDOFF_BLEND_S);
    if (k < 1) return mixPose(handoff.from, pose, easeOutCubic(k));
  }
  return pose;
}
